import type { LogLevel, LoggerConfig, LogEntry } from "../types";
import { shouldLog, shouldLogInEnvironment } from "../levels/levels";
import { createFormatter } from "../formatter/Formatter";
import { createConsoleTransport } from "../transports/ConsoleTransport";
import { sanitizeMetadata } from "./sanitizer";
import { RateLimiter } from "./rateLimiter";

export class Logger {
	private readonly config: Required<LoggerConfig>;
	private readonly rateLimiter: RateLimiter | null;

	constructor(config: LoggerConfig) {
		this.config = {
			enabled: config.enabled ?? true,
			level: config.level ?? "info",
			environment: config.environment ?? "development",
			prefix: config.prefix ?? "",
			formatter: config.formatter ?? createFormatter(),
			transports:
				config.transports && config.transports.length > 0
					? config.transports
					: [createConsoleTransport()],
			enableRateLimiting: config.enableRateLimiting ?? true,
		};

		this.rateLimiter = this.config.enableRateLimiting
			? new RateLimiter()
			: null;
	}

	private createLogEntry(
		level: LogLevel,
		message: string,
		metadata?: Record<string, unknown>,
	): LogEntry {
		return {
			timestamp: new Date(),
			level,
			message,
			prefix: this.config.prefix,
			metadata: metadata ? sanitizeMetadata(metadata) : undefined,
			environment: this.config.environment,
		};
	}

	private log(
		level: LogLevel,
		message: string,
		metadata?: Record<string, unknown>,
	): void {
		if (!this.config.enabled) {
			return;
		}

		if (!shouldLog(level, this.config.level)) {
			return;
		}

		if (!shouldLogInEnvironment(level, this.config.environment)) {
			return;
		}

		if (this.rateLimiter?.shouldSuppress(message)) {
			return;
		}

		const entry = this.createLogEntry(level, message, metadata);

		for (const transport of this.config.transports) {
			try {
				transport.log(entry, this.config.formatter);
			} catch (error) {
				console.error("Failed to log via transport:", error);
			}
		}
	}

	debug(message: string, metadata?: Record<string, unknown>): void {
		this.log("debug", message, metadata);
	}

	info(message: string, metadata?: Record<string, unknown>): void {
		this.log("info", message, metadata);
	}

	warn(message: string, metadata?: Record<string, unknown>): void {
		this.log("warn", message, metadata);
	}

	error(message: string, metadata?: Record<string, unknown>): void {
		this.log("error", message, metadata);
	}
}

export function createLogger(config: Partial<LoggerConfig>): Logger {
	return new Logger(config as LoggerConfig);
}
